import React from 'react';
import { GiftIcon, MessageCircleIcon, SparklesIcon } from 'lucide-react';
import { BonusCard } from '../components/BonusCard';
import { HowItWorksSection } from '../components/HowItWorksSection';
import { HeroSection } from '../components/HeroSection';
import { OutcomeSection } from '../components/OutcomeSection';
import { PlanSelector } from '../components/PlanSelector';
import { ProblemSection } from '../components/ProblemSection';
import { ProductShowcase } from '../components/ProductShowcase';
import { PurchasePopup } from '../components/PurchasePopup';
import { SavingsComparison } from '../components/SavingsComparison';
import { TestimonialCard } from '../components/TestimonialCard';
import { UrgencyBanner } from '../components/UrgencyBanner';
import { WeekFlowSection } from '../components/WeekFlowSection';
import { WhatsAppButton } from '../components/WhatsAppButton';
const bonusItems = [{
  title: 'Efemérides Argentinas',
  description: 'Propuestas claras para las fechas importantes, sin tener que salir a buscar ideas a último momento.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/xirY2sC8T246NNhKPASfgi/WhatsApp_Image_2026-01-14_at_03.22.59.jpg'
}, {
  title: 'Moldes para Goma Eva',
  description: 'Carteleras, personajes, letras y recursos para crear en la sala con una base lista.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/aH5WKxysy9cgYLyGPcLitQ/ChatGPT_Image_14_ene_2026,_02_26_57.png'
}, {
  title: 'Audiocuentos Infantiles',
  description: 'Un recurso extra para momentos de escucha, calma, rutinas y propuestas literarias.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9ufftsduoaev6fdx5MEotb/bonus_3.jpg'
}, {
  title: 'Cuentos Imprimibles',
  description: 'Material preparado para que siempre tengas una propuesta de lectura a mano.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/fF2JS8cT2USKxFnziNBqtQ/bonus_4.jpg'
}];
const testimonials = [{
  name: 'María Fernández',
  city: 'Buenos Aires',
  comment: 'Dejé de pasar los domingos buscando ideas. Ahora entro al pack, elijo y ya tengo una propuesta lista. Me devolvió muchísimo tiempo.',
  accent: 'orange' as const
}, {
  name: 'Laura Gómez',
  city: 'Córdoba',
  comment: 'La diferencia es llegar a la sala más tranquila. Tengo opciones para elegir y no siento que empiezo cada semana desde cero.',
  accent: 'pink' as const
}, {
  name: 'Sofía Martínez',
  city: 'Rosario',
  comment: 'Con todo lo que trae, dejé de comprar materiales sueltos. Es como tener un respaldo para todo el año en la computadora.',
  accent: 'sky' as const
}];
export function LandingPage() {
  return <div className="min-h-screen overflow-x-hidden bg-white font-sans text-slate-900" data-id="element-269">
      <UrgencyBanner data-id="element-270" />
      <WhatsAppButton data-id="element-271" />
      <PurchasePopup data-id="element-272" />

      <main className="pt-8 md:pt-10" data-id="element-273">
        <HeroSection data-id="element-274" />
        <ProductShowcase data-id="element-275" />
        <ProblemSection data-id="element-276" />
        <WeekFlowSection data-id="element-277" />
        <ProductShowcase variant="details" data-id="element-278" />
        <SavingsComparison data-id="element-279" />
        <HowItWorksSection data-id="element-280" />
        <OutcomeSection data-id="element-281" />

        <section className="bg-white py-10 sm:py-14 md:py-16" data-id="element-282">
          <div className="container mx-auto px-4 md:px-6" data-id="element-283">
            <div className="mx-auto max-w-3xl text-center" data-id="element-284">
              <div className="inline-flex items-center gap-2 rounded-full bg-pink-100 px-3 py-1.5 text-xs font-extrabold uppercase tracking-wide text-pink-700" data-id="element-285">
                <GiftIcon className="h-4 w-4" data-id="element-286" />
                Incluidos sin costo extra
              </div>
              <h2 className="mt-3 text-3xl font-black leading-tight tracking-tight text-slate-900 sm:text-4xl" data-id="element-287">
                Además, te llevás 4 bonus para sentirte todavía más acompañada.
              </h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg" data-id="element-288">
                No son relleno: son recursos para esos momentos en que necesitás
                una idea lista, ya.
              </p>
            </div>
            <div className="mx-auto mt-7 grid max-w-5xl gap-3 sm:grid-cols-2 sm:gap-4" data-id="element-289">
              {bonusItems.map((bonus, index) => <BonusCard key={bonus.title} number={index + 1} {...bonus} delay={index * 0.05} data-id="element-290" />)}
            </div>
          </div>
        </section>

        <section id="pricing" className="relative overflow-hidden bg-gradient-to-b from-orange-50 via-yellow-50 to-pink-50 py-11 sm:py-14 md:py-20" data-id="element-291">
          <div className="pointer-events-none absolute -left-20 top-20 h-56 w-56 rounded-full bg-yellow-200/70 blur-3xl" data-id="element-292" />
          <div className="pointer-events-none absolute -right-20 bottom-12 h-64 w-64 rounded-full bg-pink-200/60 blur-3xl" data-id="element-293" />
          <div className="container relative mx-auto px-4 md:px-6" data-id="element-294">
            <div className="mx-auto max-w-3xl text-center" data-id="element-295">
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-600" data-id="element-296">
                Una decisión que se siente cada semana
              </p>
              <h2 className="mt-2 text-3xl font-black leading-tight tracking-tight text-slate-900 sm:text-5xl" data-id="element-297">
                Dejá de comprar planificaciones sueltas. Tené el año mucho más
                resuelto desde hoy.
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg" data-id="element-298">
                Más calma para preparar. Más tiempo para vos. Más seguridad para
                entrar al aula.
              </p>
            </div>

            <div className="mx-auto mt-5 grid w-full max-w-sm grid-cols-2 gap-2 text-center" data-id="element-299">
              <span className="flex min-h-10 items-center justify-center rounded-xl border border-yellow-200 bg-white/90 px-2 py-2 text-xs font-extrabold leading-tight text-slate-700 shadow-sm" data-id="element-300">
                Una compra, todo el año
              </span>
              <span className="flex min-h-10 items-center justify-center rounded-xl border border-pink-200 bg-white/90 px-2 py-2 text-xs font-extrabold leading-tight text-slate-700 shadow-sm" data-id="element-301">
                Sin volver a empezar de cero
              </span>
              <span className="col-span-2 flex min-h-9 items-center justify-center rounded-xl border border-orange-200 bg-white/90 px-3 py-2 text-xs font-extrabold text-slate-700 shadow-sm" data-id="element-302">
                Acceso inmediato
              </span>
            </div>

            <div className="relative mx-auto mt-5 max-w-2xl" data-id="element-303">
              <div className="pointer-events-none absolute -inset-4 rounded-[2.5rem] bg-white/60 blur-xl" data-id="element-304" />
              <div className="relative" data-id="element-305">
                <PlanSelector data-id="element-306" />
              </div>
            </div>

            <div className="mx-auto mt-6 max-w-2xl text-center" data-id="element-307">
              <a href="https://wa.link/ip1ujo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-green-700 underline decoration-green-300 underline-offset-4" data-id="element-308">
                <MessageCircleIcon className="h-4 w-4" data-id="element-309" />
                ¿Tenés una duda antes de comprar? Escribinos por WhatsApp.
              </a>
              <p className="mt-3 text-xs leading-relaxed text-slate-500" data-id="element-310">
                Producto digital · Acceso inmediato · Recibís el acceso por
                email luego del pago · No se envían productos físicos.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white py-10 sm:py-14 md:py-16" data-id="element-311">
          <div className="container mx-auto px-4 md:px-6" data-id="element-312">
            <div className="mx-auto max-w-3xl text-center" data-id="element-313">
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-600" data-id="element-314">
                Docentes que ya respiran un poco más tranquilas
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl" data-id="element-315">
                Cuando dejás de resolver todo desde cero, se nota.
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base" data-id="element-316">
                Experiencias compartidas por docentes que incorporaron el Mega
                Pack a su planificación.
              </p>
            </div>
            <div className="mx-auto mt-6 grid max-w-5xl gap-3 md:grid-cols-3" data-id="element-317">
              {testimonials.map((testimonial, index) => <TestimonialCard key={testimonial.name} {...testimonial} delay={index * 0.06} data-id="element-318" />)}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-slate-900 py-9 text-center text-slate-300" data-id="element-319">
        <div className="container mx-auto px-4 md:px-6" data-id="element-320">
          <SparklesIcon className="mx-auto h-5 w-5 text-yellow-300" data-id="element-321" />
          <p className="mt-2 text-lg font-black text-white" data-id="element-322">
            Menos tiempo planificando. Más tiempo enseñando y viviendo.
          </p>
          <p className="mt-2 text-sm text-slate-400" data-id="element-323">
            Mega Pack Nivel Inicial · Tu respaldo para todo el año.
          </p>
        </div>
      </footer>
    </div>;
}